import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { formatDateShort, formatTime } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Upload, Trash2, Copy, Loader2, Save, X, Info, Image as ImageIcon,
  FileText, Film, Music, ExternalLink,
} from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,audio/mpeg,audio/wav,application/pdf";

const TYPE_OPTIONS = [
  { value: "", label: "Todos los formatos" },
  { value: "image", label: "Imágenes (JPG/PNG/WebP)" },
  { value: "gif", label: "GIF" },
  { value: "svg", label: "SVG" },
  { value: "video", label: "Video (MP4/WebM)" },
  { value: "audio", label: "Audio (MP3/WAV)" },
  { value: "pdf", label: "PDF" },
];

function humanSize(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function iconForMime(mime) {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return Film;
  if (mime.startsWith("audio/")) return Music;
  return FileText;
}

export default function AdminMediaPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [mimeGroup, setMimeGroup] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // { name, progress, status }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [usage, setUsage] = useState(null); // { count, articles }
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.adminListMedia({
      q: q || undefined,
      mime_group: mimeGroup || undefined,
      date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
      limit: 120,
    })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [q, mimeGroup, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.adminMediaStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, mimeGroup, dateFrom, dateTo, load]);

  const openDetail = async (m) => {
    setSelected({ ...m, _dirty: false });
    setUsage(null);
    try {
      const u = await api.adminMediaUsage(m.id);
      setUsage(u);
    } catch { /* ignore */ }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const initialQueue = Array.from(files).map((f, i) => ({
      _uid: `upl-${Date.now()}-${i}-${f.name}`,
      name: f.name, progress: 0, status: "pending",
    }));
    setUploadQueue(initialQueue);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadQueue((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "uploading" } : x));
      try {
        await api.adminUploadMedia(f, {}, (pct) => {
          setUploadQueue((prev) => prev.map((x, idx) => idx === i ? { ...x, progress: pct } : x));
        });
        setUploadQueue((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "done", progress: 100 } : x));
      } catch (e) {
        const d = e?.response?.data?.detail;
        setUploadQueue((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "error", error: d || "Error" } : x));
        toast.error(`${f.name}: ${typeof d === "string" ? d : "no se pudo subir"}`);
      }
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    toast.success("Subida completada.");
    load();
    // Clear queue after 4s if everything ok
    setTimeout(() => setUploadQueue((q) => q.filter((x) => x.status === "error")), 4000);
  };

  // Drag & drop on the whole page (drop area)
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      stop(e);
      const files = e.dataTransfer?.files;
      if (files && files.length) uploadFiles(files);
    };
    el.addEventListener("dragover", stop);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", stop);
      el.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMeta = async () => {
    if (!selected) return;
    try {
      const payload = {
        alt: selected.alt || "",
        description: selected.description || "",
        caption: selected.caption || "",
        credit: selected.credit || "",
        author: selected.author || "",
        focal_x: selected.focal_x ?? 50,
        focal_y: selected.focal_y ?? 50,
      };
      const updated = await api.adminUpdateMedia(selected.id, payload);
      toast.success("Cambios guardados.");
      setSelected({ ...updated, _dirty: false });
      setItems((prev) => prev.map((x) => x.id === updated.id ? updated : x));
    } catch {
      toast.error("No se pudo guardar.");
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    const doDelete = async (force = false) => {
      try {
        await api.adminDeleteMedia(confirmDelete.id, force);
        toast.success(force ? "Archivo eliminado (referencias rotas)" : "Archivo eliminado.");
        setConfirmDelete(null);
        setSelected(null);
        load();
      } catch (e) {
        const status = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (!force && status === 409 && typeof d === "object" && d?.requires_force) {
          const msg = d.message || `Hay ${d.linked_count || "varias"} noticias que lo usan.`;
          if (window.confirm(`${msg}\n\n¿Continuar y eliminar de todos modos?`)) return doDelete(true);
          setConfirmDelete(null);
          return;
        }
        toast.error(typeof d === "string" ? d : (d?.message || "No se pudo eliminar."));
      }
    };
    await doDelete(false);
  };

  const copyUrl = async (u) => {
    try {
      await navigator.clipboard.writeText(u);
      toast.success("URL copiada.");
    } catch { toast.error("No se pudo copiar."); }
  };

  const setFocalFromClick = (e) => {
    if (!selected || !selected.mime?.startsWith("image/")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    setSelected((s) => ({ ...s, focal_x: x, focal_y: y, _dirty: true }));
  };

  return (
    <div data-testid={ATID.mediaPage} ref={dropRef}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Recursos</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Multimedia</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(e) => uploadFiles(e.target.files)}
            data-testid={ATID.mediaUploadInput}
            className="hidden"
          />
          <Button
            data-testid={ATID.mediaUploadButton}
            onClick={() => fileRef.current?.click()}
            className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 px-4 uppercase text-xs tracking-widest font-semibold"
          >
            <Upload className="w-4 h-4 mr-2" /> Subir archivos
          </Button>
        </div>
      </div>

      {status && !status.ok && (
        <div className="mb-4 bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 text-sm flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5" />
          <div>Almacenamiento no disponible: {status.message}</div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-softblue p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="lg:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
          <Input
            data-testid={ATID.mediaFilterQ}
            placeholder="Buscar por nombre, alt, crédito o descripción…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 bg-lightgray border-softblue"
          />
        </div>
        <select
          data-testid={ATID.mediaFilterType}
          value={mimeGroup}
          onChange={(e) => setMimeGroup(e.target.value)}
          className="h-10 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
        >
          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <Input
            data-testid={ATID.mediaFilterDateFrom}
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-10 bg-lightgray border-softblue"
          />
          <Input
            data-testid={ATID.mediaFilterDateTo}
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-10 bg-lightgray border-softblue"
          />
        </div>
      </div>

      {/* Upload queue */}
      {uploadQueue.length > 0 && (
        <div className="bg-white border border-softblue p-4 mb-6">
          <div className="text-xs uppercase tracking-widest text-textgray font-semibold mb-3">Cargando</div>
          <div className="space-y-2">
            {uploadQueue.map((u, i) => (
              <div key={u._uid || `upl-${i}-${u.name}`} className="text-xs">
                <div className="flex items-center justify-between gap-4">
                  <span className="truncate font-mono">{u.name}</span>
                  <span className={`uppercase tracking-widest font-semibold ${u.status === "done" ? "text-watergreen" : u.status === "error" ? "text-red-600" : "text-textgray"}`}>
                    {u.status === "done" ? "Listo" : u.status === "error" ? "Error" : `${u.progress}%`}
                  </span>
                </div>
                {u.status !== "done" && u.status !== "error" && (
                  <Progress value={u.progress} className="mt-1 h-1.5" />
                )}
                {u.status === "error" && <div className="text-red-600 mt-1">{u.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Grid */}
        <div className={selected ? "lg:col-span-2" : "lg:col-span-3"}>
          <div className="flex items-center justify-between mb-3 text-sm text-textgray">
            <span>{total} archivo{total === 1 ? "" : "s"}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>

          {loading && items.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-square bg-softblue/40 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-softblue p-10 text-center text-sm text-textgray">
              Todavía no subiste archivos. Arrastrá imágenes acá o hacé clic en "Subir archivos".
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {items.map((m) => {
                const Icon = iconForMime(m.mime);
                const isImg = m.mime?.startsWith("image/");
                const thumb = m.variants?.thumbnail?.url || m.url;
                const isSel = selected?.id === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => openDetail(m)}
                    data-testid={ATID.mediaCard(m.id)}
                    className={`aspect-square bg-lightgray border relative overflow-hidden group ${
                      isSel ? "border-nightblue ring-2 ring-turquoise" : "border-softblue hover:border-nightblue"
                    }`}
                  >
                    {isImg ? (
                      <img src={thumb} alt={m.alt || m.original_name} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-3 text-textgray">
                        <Icon className="w-8 h-8" />
                        <span className="mt-2 text-[10px] uppercase tracking-widest font-mono">{m.mime.split("/")[1]}</span>
                        <span className="mt-1 text-[10px] text-center line-clamp-2 px-1">{m.original_name}</span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-nightblue/85 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-[10px] text-white truncate">{m.original_name}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <aside data-testid={ATID.mediaDetailPanel} className="bg-white border border-softblue p-5 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-turquoise font-semibold">Detalles</div>
                <h3 className="font-serif text-lg font-bold text-blueblack truncate">{selected.original_name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-textgray hover:text-nightblue">
                <X className="w-4 h-4" />
              </button>
            </div>

            {selected.mime?.startsWith("image/") ? (
              <div
                className="mt-4 relative bg-lightgray border border-softblue overflow-hidden cursor-crosshair"
                onClick={setFocalFromClick}
                title="Clic para fijar el punto focal"
                data-testid={ATID.mediaDetailFocal}
              >
                <img src={selected.url} alt="" className="w-full h-auto" />
                <div
                  className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-lg bg-watergreen rounded-full pointer-events-none"
                  style={{ left: `${selected.focal_x ?? 50}%`, top: `${selected.focal_y ?? 50}%` }}
                />
              </div>
            ) : selected.mime?.startsWith("video/") ? (
              <video src={selected.url} controls className="mt-4 w-full border border-softblue bg-lightgray" />
            ) : selected.mime?.startsWith("audio/") ? (
              <audio src={selected.url} controls className="mt-4 w-full" />
            ) : (
              <div className="mt-4">
                <a href={selected.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-petroleum hover:text-turquoise text-sm">
                  <ExternalLink className="w-4 h-4" /> Abrir archivo
                </a>
              </div>
            )}

            <dl className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between gap-4">
                <dt className="text-textgray uppercase tracking-widest">Formato</dt>
                <dd className="font-mono text-nightblue">{selected.mime}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-textgray uppercase tracking-widest">Tamaño</dt>
                <dd className="text-nightblue">{humanSize(selected.size)}</dd>
              </div>
              {selected.width && selected.height && (
                <div className="flex justify-between gap-4">
                  <dt className="text-textgray uppercase tracking-widest">Dimensiones</dt>
                  <dd className="text-nightblue">{selected.width} × {selected.height}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-textgray uppercase tracking-widest">Subido</dt>
                <dd className="text-nightblue">{formatDateShort(selected.uploaded_at)} · {formatTime(selected.uploaded_at)}</dd>
              </div>
              {selected.uploaded_by_name && (
                <div className="flex justify-between gap-4">
                  <dt className="text-textgray uppercase tracking-widest">Por</dt>
                  <dd className="text-nightblue">{selected.uploaded_by_name}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-textgray uppercase tracking-widest">Foco</dt>
                <dd className="text-nightblue">{selected.focal_x ?? 50}%, {selected.focal_y ?? 50}%</dd>
              </div>
            </dl>

            <div className="mt-3 flex gap-2">
              <button
                onClick={() => copyUrl(selected.url)}
                data-testid={ATID.mediaDetailCopyUrl}
                className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase tracking-widest border border-softblue h-8 px-2 hover:bg-lightgray"
              >
                <Copy className="w-3.5 h-3.5" /> Copiar URL
              </button>
              <a
                href={selected.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase tracking-widest border border-softblue h-8 px-2 hover:bg-lightgray"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Ver
              </a>
            </div>

            <div className="mt-5 space-y-3 border-t border-softblue pt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Texto alternativo</Label>
                <Input
                  data-testid={ATID.mediaDetailAlt}
                  value={selected.alt || ""}
                  onChange={(e) => setSelected({ ...selected, alt: e.target.value, _dirty: true })}
                  className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción</Label>
                <Textarea
                  data-testid={ATID.mediaDetailDescription}
                  value={selected.description || ""}
                  onChange={(e) => setSelected({ ...selected, description: e.target.value, _dirty: true })}
                  rows={2}
                  className="mt-1 bg-lightgray border-softblue text-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Pie de imagen</Label>
                <Input
                  data-testid={ATID.mediaDetailCaption}
                  value={selected.caption || ""}
                  onChange={(e) => setSelected({ ...selected, caption: e.target.value, _dirty: true })}
                  className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Crédito</Label>
                  <Input
                    data-testid={ATID.mediaDetailCredit}
                    value={selected.credit || ""}
                    onChange={(e) => setSelected({ ...selected, credit: e.target.value, _dirty: true })}
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Autor</Label>
                  <Input
                    data-testid={ATID.mediaDetailAuthor}
                    value={selected.author || ""}
                    onChange={(e) => setSelected({ ...selected, author: e.target.value, _dirty: true })}
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                  />
                </div>
              </div>
              <Button
                data-testid={ATID.mediaDetailSave}
                onClick={saveMeta}
                disabled={!selected._dirty}
                className="w-full bg-nightblue hover:bg-blueblack text-white rounded-none h-9 uppercase text-xs tracking-widest font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" /> Guardar cambios
              </Button>
            </div>

            <div data-testid={ATID.mediaDetailUsage} className="mt-5 border-t border-softblue pt-4">
              <div className="text-xs uppercase tracking-widest text-textgray font-semibold mb-2">Usado en</div>
              {usage === null ? (
                <div className="text-xs text-textgray">Verificando…</div>
              ) : usage.count === 0 ? (
                <div className="text-xs text-textgray">No se usa en ninguna noticia.</div>
              ) : (
                <ul className="space-y-1.5">
                  {usage.articles.map((a) => (
                    <li key={a.id} className="text-xs">
                      <Link to={`/admin/noticias/${a.id}`} className="text-petroleum hover:text-turquoise line-clamp-2">
                        {a.title}
                      </Link>
                      <span className="text-textgray"> · {a.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-softblue">
              <Button
                data-testid={ATID.mediaDetailDelete}
                variant="outline"
                onClick={() => setConfirmDelete(selected)}
                className="w-full rounded-none border-red-300 text-red-600 hover:bg-red-50 h-9 uppercase text-xs tracking-widest font-semibold"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Eliminar archivo
              </Button>
            </div>
          </aside>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar archivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{confirmDelete?.original_name}</strong> del almacenamiento y no podrá recuperarse.
              Si el archivo está en uso, la operación será rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
